import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../../providers/TenantProvider';

export interface OrganizationSettings {
  general: {
    timezone: string;
    dateFormat: string;
    language: string;
  };
  security: {
    requireTwoFactor: boolean;
    sessionTimeoutMinutes: number;
    allowedIpAddresses: string[];
    passwordPolicy: {
      minLength: number;
      requireUppercase: boolean;
      requireNumbers: boolean;
      requireSpecialChars: boolean;
    };
  };
  notifications: {
    emailNotifications: boolean;
    activityAlerts: boolean;
    securityAlerts: boolean;
    weeklyDigest: boolean;
    marketingEmails: boolean;
  };
}

export function useSettings() {
  const { getAccessTokenSilently } = useAuth0();
  const { currentTenant } = useTenant();
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    if (!currentTenant) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/settings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      setSettings(data.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, getAccessTokenSilently]);

  const updateGeneralSettings = useCallback(
    async (updates: Partial<OrganizationSettings['general']>) => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/settings/general`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-tenant-id': currentTenant.id,
            },
            body: JSON.stringify(updates),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to update settings');
        }

        const data = await response.json();
        setSettings((prev) => (prev ? { ...prev, general: data.settings } : null));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  const updateSecuritySettings = useCallback(
    async (updates: Partial<OrganizationSettings['security']>) => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/settings/security`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-tenant-id': currentTenant.id,
            },
            body: JSON.stringify(updates),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to update security settings');
        }

        const data = await response.json();
        setSettings((prev) => (prev ? { ...prev, security: data.settings } : null));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  const updateNotificationSettings = useCallback(
    async (updates: Partial<OrganizationSettings['notifications']>) => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/settings/notifications`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-tenant-id': currentTenant.id,
            },
            body: JSON.stringify(updates),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to update notification settings');
        }

        const data = await response.json();
        setSettings((prev) => (prev ? { ...prev, notifications: data.settings } : null));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  return {
    settings,
    isLoading,
    error,
    fetchSettings,
    updateGeneralSettings,
    updateSecuritySettings,
    updateNotificationSettings,
  };
}
